import { useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { Prism as SyntaxHighlighter } from "react-syntax-highlighter";
import { oneDark } from "react-syntax-highlighter/dist/esm/styles/prism";
import {
  AttachmentChip,
  MessageBubble,
  ToolCall,
} from "@fwf/fwfa-design-system/react";
import type { ChatMessage } from "@/types";
import { dsToolStatus, humanizeToolName } from "@/lib/tool-display";
import { FileGlyph, ImageGlyph } from "./AttachmentGlyphs";
import { ThinkingIndicator } from "./ThinkingIndicator";

interface Props {
  message: ChatMessage;
}

export function Message({ message }: Props) {
  const [toolOutputExpanded, setToolOutputExpanded] = useState(false);

  if (message.role === "tool") {
    const toolName = message.toolName || "tool";
    // The text pane, where ToolCallActivity takes the terminal one: this row is
    // for tool messages with no captured output stream, so the content is plain
    // text and the pane grows with it rather than sitting at a fixed height.
    return (
      <ToolCall
        name={<span title={toolName}>{humanizeToolName(toolName)}</span>}
        status={dsToolStatus(message.toolStatus)}
        pane={message.content ? "text" : undefined}
        open={toolOutputExpanded}
        onOpenChange={setToolOutputExpanded}
      >
        {message.content}
      </ToolCall>
    );
  }

  if (message.role === "user") {
    return (
      // The DS owns the bubble: right-aligned, --fwfa-radius-md, and a bordered
      // --fwfa-surface-hover fill. The old hand-rolled version was a flat navy
      // block at rounded-2xl with one corner clipped, which is why it had to pin
      // data-fwf-app-theme="dark" — a fixed navy fill cannot follow a theme, so
      // any chip landing on it would have gone pale. This fill is a token, so the
      // pin is gone with it.
      <MessageBubble variant="user" className="whitespace-pre-wrap">
        {message.attachments && message.attachments.length > 0 && (
          <div className="fwfa-attachment-list">
            {message.attachments.map((attachment) => (
              <AttachmentChip
                key={attachment.id}
                name={attachment.name}
                icon={
                  attachment.kind === "image" ? <ImageGlyph /> : <FileGlyph />
                }
              />
            ))}
          </div>
        )}
        {message.content}
      </MessageBubble>
    );
  }

  return (
    // The assistant turn is not a bubble. `.fwfa-message--assistant` is flat text
    // on the page in --fwfa-text-soft, no fill and no border, so the eye reads a
    // conversation rather than a stack of tinted panels.
    //
    // Reasoning is a SIBLING of the prose, not a child of it. Nested, it would
    // inherit the message's shrink-to-fit width and land a few rem narrower than
    // the tool-call rows around it; as its own stack item it takes the same
    // min(100%, 42rem) they do, so every machine-voice row in the thread lines up.
    <>
      {message.thinking && <ThinkingIndicator content={message.thinking} />}
      <MessageBubble variant="assistant">
        <ReactMarkdown
          remarkPlugins={[remarkGfm]}
          components={{
            code({ className, children, ref: _ref, node: _node, ...props }) {
              const match = /language-(\w+)/.exec(className || "");
              const codeString = String(children).replace(/\n$/, "");

              if (match) {
                return (
                  <div className="my-2 rounded-lg overflow-hidden border border-navy-light">
                    <div className="flex items-center justify-between px-3 py-1.5 bg-navy-dark text-stone-400 text-xs">
                      <span className="font-mono text-[10px]">{match[1]}</span>
                      <button
                        onClick={() =>
                          window.freshvibe.copyToClipboard(codeString)
                        }
                        className="hover:text-gold transition-colors font-mono text-[10px]"
                      >
                        Copy
                      </button>
                    </div>
                    <SyntaxHighlighter
                      style={oneDark}
                      language={match[1]}
                      PreTag="div"
                      customStyle={{
                        margin: 0,
                        borderRadius: 0,
                        fontSize: "0.75rem",
                        background: "#001a24",
                      }}
                    >
                      {codeString}
                    </SyntaxHighlighter>
                  </div>
                );
              }

              return (
                <code
                  className="px-1.5 py-0.5 rounded bg-stone-200 dark:bg-navy-light text-xs font-mono text-navy dark:text-gold"
                  {...props}
                >
                  {children}
                </code>
              );
            },
            // Colour and leading now come from `.fwfa-message` (--fwfa-text-soft,
            // --fwfa-leading-body) and vertical rhythm from its grid gap, so the
            // block children carry neither. The stone/navy overrides that used to
            // sit here were only ever there to stay legible on the old bubble fill.
            p({ children }) {
              return <p>{children}</p>;
            },
            ul({ children }) {
              return <ul className="pl-4 list-disc">{children}</ul>;
            },
            ol({ children }) {
              return <ol className="pl-4 list-decimal">{children}</ol>;
            },
            li({ children }) {
              return <li className="mb-1">{children}</li>;
            },
            a({ href, children }) {
              return (
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (href) window.freshvibe.openExternal(href);
                  }}
                  className="text-gold hover:text-gold-dark underline underline-offset-2"
                >
                  {children}
                </a>
              );
            },
            h1({ children }) {
              return (
                <h1 className="font-display text-lg font-bold text-navy dark:text-white">
                  {children}
                </h1>
              );
            },
            h2({ children }) {
              return (
                <h2 className="font-display text-base font-semibold text-navy dark:text-white">
                  {children}
                </h2>
              );
            },
            h3({ children }) {
              return (
                <h3 className="font-display text-sm font-semibold text-navy dark:text-white">
                  {children}
                </h3>
              );
            },
            blockquote({ children }) {
              return (
                <blockquote className="border-l-2 border-gold pl-3 text-stone-500 dark:text-stone-400">
                  {children}
                </blockquote>
              );
            },
          }}
        >
          {message.content}
        </ReactMarkdown>
      </MessageBubble>
    </>
  );
}
